import { useLazyQuery } from "@apollo/client";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid";
import { GET_PRODUCT_WITH_PRICEBOOK } from "../GraphQLModules/Header/query";
import { Suspense, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { constantData } from "../../../Assets/constants";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { currencyFormatterWithDecimal } from "../../../Assets/formatter";
import Components from "../../../Components";

const SearchBar = () => {
  const navigate = useNavigate();
  const customTheme = useTheme();
  const checkFirstRender = useRef(false);
  const debounceTimeout = useRef(null);
  const abortControllerRef = useRef(null);
  const needToFetchData = useRef(null);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [openSearchDropdown, setOpenSearchDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [searchProducts, { loading, data }] = useLazyQuery(
    GET_PRODUCT_WITH_PRICEBOOK,
    {
      onCompleted: (data) => {
        needToFetchData.current =
          data && data.searchProductsWithPriceBook.items?.length > 0
            ? true
            : false;

        const items = data.searchProductsWithPriceBook.items;
        const optionsToShow =
          data.searchProductsWithPriceBook.total_count > 20
            ? [
              ...items,
              {
                id: "view_more",
                name: `${data.searchProductsWithPriceBook.total_count - 20
                  } more`,
              },
            ]
            : items;
        if (checkFirstRender.current) {
          setOpenSearchDropdown(true);
        }
        checkFirstRender.current = false;
        setSearchResult(optionsToShow);
      },
      onError: (error) => {
        console.log("error", error.message);
      },
      fetchPolicy: "no-cache",
    }
  );

  const handleClose = () => {
    console.log("handleClose()");
    //setSearchValue("");
    setOpenSearchDropdown(false);
    setShowBackdrop(false);
    setSearchResult([]);
  };

  console.log("searchResult", searchResult);

  const handleInputChange = (event, value) => {
    console.log("handleInputChange()");
    //const value = event?.target?.value;
    setSearchValue(value);
    //setOpenSearchDropdown(true);
    if (value === undefined) {
      event?.preventDefault();
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (value?.length === 0 || !value) {
      setSearchResult([]);
      return;
    }

    abortControllerRef.current = new AbortController();

    if (
      value &&
      ((value.length > 2 && needToFetchData.current) ||
        value.length < searchValue.length)
    ) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        searchProducts({
          variables: {
            name: value,
            pageNumber: 1,
            limit: 20,
          },
          context: {
            fetchOptions: {
              signal: abortControllerRef.current.signal,
            },
          },
        });
      }, 800);
    }
  };

  const handleListItemClick = (item) => {
    console.log("openModalOnListItemClick()");
    console.log("searchProductItem", item);
    handleClose();
    // inputRef.current?.blur();
    setTimeout(() => {
      navigate(`/stock/product-detail/${item.id}`);
    }, 50);
  };

  const getTextField = () => {
    console.log("getTextField()");
    const baseStyle = {
      borderRadius: customTheme.dimensions.dimen10,
      tranasition: constantData.ANIMATION.BORDER_RADIUS_30S_EASE_IN_OUT,
      bgcolor: constantData.STYLING.BG_COLOR,
      justifyContent: customTheme.alignment.justifyContent.center,
      alignItems: customTheme.alignment.alignItems.center,
      fontSize: customTheme.dimensions.dimen16,
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: customTheme.border.borderWidth,
        borderColor: customTheme.border.borderColour.borderDivider,
      },
    };
    if (openSearchDropdown) {
      return {
        ...baseStyle,
        tranasition: constantData.ANIMATION.BORDER_RADIUS_30S_EASE_IN_OUT,
        borderBottomLeftRadius: customTheme.dimensions.dimen0,
        borderBottomRightRadius: customTheme.dimensions.dimen0,
      };
    }
    return baseStyle;
  };

  const handleViewAllClick = () => {
    console.log("handleViewAllClick()");
    handleClose();
    // inputRef.current?.blur();
    setTimeout(() => {
      navigate("/products", {
        state: {
          searchValue: searchValue,
        },
      });
    }, 50);
  };

  return (
    <>
      {showBackdrop && (
        <Box
          sx={(theme) => ({
            position: theme.alignment.position.fixed,
            top: 0,
            left: 0,
            width: theme.dimensions.percent.dimen100,
            zIndex: theme.size.zIndex.zIndex1,
            height: theme.dimensions.percent.dimen100,
            backgroundColor: theme.palette.background.blur,
          })}
        ></Box>
      )}
      <Paper
        elevation={customTheme.size.elevation.elevation1}
        sx={(theme) => ({
          width: theme.dimensions.dimenInherit,
          position: theme.alignment.position.relative,
          justifyContent: theme.alignment.justifyContent.center,
          zIndex: showBackdrop && theme.size.zIndex.zIndex1,
          alignItems: theme.alignment.alignItems.center,
          borderRadius: theme.dimensions.dimen10,
          display: theme.display.displayFlex,
        })}
      >
        <Grid
          item
          sx={(theme) => ({
            width: theme.dimensions.percent.dimen100,
            position: theme.alignment.position.absolute,
            alignItems: theme.alignment.alignItems.center,
            borderRadius: theme.dimensions.dimen10,
          })}
        >
          <Autocomplete
            componentsProps={{
              paper: {
                sx: {
                  width: customTheme.dimensions.dimenInherit,
                  borderTopLeftRadius: customTheme.dimensions.dimen0,
                  borderTopRightRadius: customTheme.dimensions.dimen0,
                },
              },
            }}
            filterOptions={(x) => x}
            value={null}
            open={openSearchDropdown}
            noOptionsText={loading ? " " : constantData.NO_PRODUCT_FOUND}
            loadingText={"loading...."}
            options={searchResult || []}
            inputValue={searchValue}
            onChange={(event, newValue) => {
              if (!newValue) return;
              if (newValue.id === "view_more") {
                handleViewAllClick();
                return;
              }
              setSearchValue("");
              // inputRef.current?.blur();
              handleListItemClick(newValue);
            }}
            onInputChange={handleInputChange}
            loading={loading}
            onOpen={() => {
              checkFirstRender.current = true;
              searchProducts({
                variables: {
                  pageNumber: 1,
                  limit: 20,
                },
              });
              setShowBackdrop(true);
            }}
            onClose={handleClose}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            renderOption={(props, option, { inputValue }) => {
              const matches =
                match(option?.name ?? "", inputValue, {
                  insideWords: true,
                }) || [];
              const parts = parse(option?.name, matches);
              return (
                <Suspense
                  fallback={
                    <ListItem sx={{ height: "70px" }} {...props}>
                      <Skeleton
                        variant="circular"
                        width={40}
                        height={40}
                        sx={{ mr: 2 }}
                      />
                      <Skeleton variant="text" width="80%" />
                    </ListItem>
                  }
                >
                  {option?.akeneo_token ? (
                    <ListItem
                      {...props}
                      key={option.id}
                      onClick={() => handleListItemClick(option)}
                    >
                      <ListItemAvatar>
                        <Components.AkeneoImage
                          token={option.akeneo_token}
                          url={option.thumbnail_url}
                          variant={constantData.AVTAR_VARIANT.ROUNDED}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack spacing={customTheme.dimensions.dimen4}>
                            <Typography
                              variant={
                                constantData.TYPOGRAPHY_VARIANT.BODY_MEDIUM
                              }
                            >
                              {parts.map((part, index) => (
                                <span
                                  key={index}
                                  style={{
                                    fontWeight: part.highlight ? 700 : 400,
                                  }}
                                >
                                  {part.text}
                                </span>
                              ))}
                            </Typography>
                            <Stack
                              direction={customTheme.alignment.direction.row}
                            >
                              <Components.DualTextComponent
                                leftText={`${constantData.MRP}`}
                                leftTextVariant={
                                  constantData.TYPOGRAPHY_VARIANT.LABLE_MEDIUM
                                }
                                leftTextColor={
                                  customTheme.palette.greyText.main
                                }
                                rightTextVariant={
                                  constantData.TYPOGRAPHY_VARIANT.LABLE_MEDIUM
                                }
                                // rightText={`${option.mrp} ${"\u00A0|\u00A0"}`}
                                rightText={
                                  currencyFormatterWithDecimal(
                                    option.mrp || 0
                                  ) + "\u00A0|\u00A0"
                                }
                                rightTextColor={
                                  customTheme.palette.secondary.main
                                }
                                direction={customTheme.alignment.direction.row}
                                spacing={customTheme.dimensions.dimen5}
                              />
                              <Divider
                                orientation={
                                  customTheme.alignment.orientation.vertical
                                }
                              />
                              <Components.DualTextComponent
                                leftText={`${option.quantity_per_ctn} ${constantData.PCS}`}
                                leftTextVariant={
                                  constantData.TYPOGRAPHY_VARIANT.LABLE_MEDIUM
                                }
                                leftTextColor={
                                  customTheme.palette.secondary.main
                                }
                                rightTextVariant={
                                  constantData.TYPOGRAPHY_VARIANT.LABLE_MEDIUM
                                }
                                rightText={`per ${constantData.CTN}`}
                                rightTextColor={
                                  customTheme.palette.greyText.main
                                }
                                direction={customTheme.alignment.direction.row}
                                spacing={customTheme.dimensions.dimen5}
                              />
                            </Stack>
                          </Stack>
                        }
                      />
                    </ListItem>
                  ) : (
                    <ListItemButton {...props} onClick={handleViewAllClick}>
                      <ListItemText
                        primary={
                          <Typography
                            variant={
                              constantData.TYPOGRAPHY_VARIANT.LABLE_MEDIUM
                            }
                            color={customTheme.palette.primary.main}
                          >
                            {option.name}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  )}
                </Suspense>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant={constantData.TEXT_FIELD_VARIANT.OUTLINED}
                placeholder={constantData.STOCK_TEXTS.SEARCH_PRODUCT}
                InputProps={{
                  ...params.InputProps,
                  sx: getTextField(),
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="success" size={20} />
                      ) : null}
                    </>
                  ),
                  startAdornment: (
                    <GridSearchIcon
                      sx={(theme) => ({
                        color: theme.palette.iconColor.black,
                        marginLeft: theme.dimensions.dimen12,
                        fontSize: theme.dimensions.dimen20,
                      })}
                    />
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Paper>
    </>
  );
};
export default SearchBar;
